import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Image, Link as LinkIcon, Type, Building2, Upload, Loader2, Save } from 'lucide-react';

export const AdvertisementForm = ({
  formData,
  setFormData,
  handleChange,
  handleSubmit,
  editingAd,
  fileInputRef,
  handleImageUpload,
  uploading,
  resetForm,
  apiBase,
}) => (
  <Card data-testid="advertisement-form" className="dark:bg-gray-800 border-red-200 dark:border-red-800">
    <CardHeader className="pb-4">
      <CardTitle className="text-lg dark:text-white">
        {editingAd ? 'Edit Advertisement' : 'Create New Advertisement'}
      </CardTitle>
    </CardHeader>
    <CardContent>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1 dark:text-white">
              <Type className="w-4 h-4 inline mr-1" /> Title *
            </label>
            <Input
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Advertisement title"
              required
              className="dark:bg-gray-700"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 dark:text-white">
              <Building2 className="w-4 h-4 inline mr-1" /> Company Name
            </label>
            <Input
              name="company_name"
              value={formData.company_name}
              onChange={handleChange}
              placeholder="Advertiser company"
              className="dark:bg-gray-700"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1 dark:text-white">
            Description
          </label>
          <Textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Short description for the ad"
            rows={2}
            className="dark:bg-gray-700"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1 dark:text-white">
              <Image className="w-4 h-4 inline mr-1" /> Image
            </label>
            <div className="flex gap-2">
              <Input
                name="image_url"
                value={formData.image_url}
                onChange={handleChange}
                placeholder="Image URL or upload"
                className="dark:bg-gray-700 flex-1"
              />
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              </Button>
            </div>
            {formData.image_url && (
              <div className="mt-2 relative inline-block">
                <img
                  src={formData.image_url.startsWith('/') ? `${apiBase.replace('/api', '')}${formData.image_url}` : formData.image_url}
                  alt="Preview"
                  className="h-20 rounded border dark:border-gray-600"
                  onError={(e) => e.target.style.display = 'none'}
                />
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 dark:text-white">
              <LinkIcon className="w-4 h-4 inline mr-1" /> Link URL
            </label>
            <Input
              name="link_url"
              value={formData.link_url}
              onChange={handleChange}
              placeholder="https://advertiser-website.com"
              className="dark:bg-gray-700"
            />
            <div className="mt-2">
              <label className="block text-sm font-medium mb-1 dark:text-white">Button Text</label>
              <Input
                name="link_text"
                value={formData.link_text}
                onChange={handleChange}
                placeholder="Learn More"
                className="dark:bg-gray-700"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1 dark:text-white">Ad Type</label>
            <select
              name="ad_type"
              value={formData.ad_type}
              onChange={handleChange}
              className="w-full p-2 border dark:border-gray-700 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="sidebar">Sidebar Ad</option>
              <option value="banner">Banner Ad</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 dark:text-white">Start Date</label>
            <Input
              type="date"
              name="start_date"
              value={formData.start_date}
              onChange={handleChange}
              className="dark:bg-gray-700"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 dark:text-white">End Date</label>
            <Input
              type="date"
              name="end_date"
              value={formData.end_date}
              onChange={handleChange}
              className="dark:bg-gray-700"
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Switch
            checked={formData.is_active}
            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
          />
          <span className="text-sm dark:text-white">Active (Show on homepage)</span>
        </div>

        <div className="flex gap-2 pt-4">
          <Button type="button" variant="outline" onClick={resetForm}>
            Cancel
          </Button>
          <Button type="submit" className="bg-red-600 hover:bg-red-700">
            <Save className="w-4 h-4 mr-1" />
            {editingAd ? 'Update' : 'Create'} Advertisement
          </Button>
        </div>
      </form>
    </CardContent>
  </Card>
);
